import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Star } from "lucide-react";
import api, { apiErr, resolveImg } from "@/lib/api";

export default function CatalogManager({ type }) {
  const isBox = type === "boxes";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(isBox ? "/boxes" : "/pizzas").then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image téléversée");
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };

  const add = async () => {
    if (!form.name || !form.image_url) { toast.error("Nom et image obligatoires"); return; }
    if (!isBox && !form.price) { toast.error("Prix obligatoire"); return; }
    setBusy(true);
    try {
      if (isBox) await api.post("/admin/boxes", { name: form.name, tagline: form.tagline || "", description: form.description || "", image_url: form.image_url, collection: form.collection || "Autres" });
      else await api.post("/admin/pizzas", { name: form.name, description: form.description || "", price: parseFloat(form.price), image_url: form.image_url, is_specialty: !!form.is_specialty });
      toast.success("Ajouté !");
      setForm({}); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/${isBox ? "boxes" : "pizzas"}/${id}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="card-cocarde p-5 h-fit">
        <h3 className="font-display text-xl cc-navy mb-3">Ajouter {isBox ? "un modèle de boîte" : "une pizza"}</h3>
        <div className="space-y-3">
          <input data-testid="cat-name" placeholder="Nom" value={form.name || ""} onChange={set("name")} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 outline-none focus:border-cc-red" />
          {isBox ? (
            <>
              <input data-testid="cat-collection" placeholder="Collection (ex: Sport, Élections 2027)" value={form.collection || ""} onChange={set("collection")} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 outline-none focus:border-cc-red" />
              <input data-testid="cat-tagline" placeholder="Slogan" value={form.tagline || ""} onChange={set("tagline")} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 outline-none focus:border-cc-red" />
            </>
          ) : (
            <>
              <input data-testid="cat-price" type="number" step="0.10" placeholder="Prix (€)" value={form.price || ""} onChange={set("price")} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 outline-none focus:border-cc-red" />
              <label className="flex items-center gap-2 text-sm font-semibold cc-navy"><input type="checkbox" checked={!!form.is_specialty} onChange={(e) => setForm({ ...form, is_specialty: e.target.checked })} /> Spécialité</label>
            </>
          )}
          <textarea data-testid="cat-desc" placeholder="Description" value={form.description || ""} onChange={set("description")} rows={2} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 outline-none focus:border-cc-red" />
          <input ref={fileRef} data-testid="cat-image" type="file" accept="image/*" onChange={upload} className="text-sm" />
          {form.image_url && <img src={resolveImg(form.image_url)} alt="aperçu" className="h-24 object-contain rounded-lg border border-slate-200" />}
          {uploading && <p className="text-sm text-slate-500 flex items-center gap-1"><Upload size={14} /> Téléversement…</p>}
          <button data-testid="cat-add" onClick={add} disabled={busy || uploading} className="btn-cocarde w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"><Plus size={16} /> Ajouter</button>
        </div>
      </div>

      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`cat-item-${it.id}`} className="card-cocarde overflow-hidden">
            <div className="h-32 bg-white grid place-items-center relative">
              <img src={resolveImg(it.image_url)} alt={it.name} className="max-h-32 object-contain" />
              {it.is_specialty && <span className="absolute top-2 left-2 bg-cc-gold text-cc-navy text-xs font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><Star size={11} /> Spécialité</span>}
            </div>
            <div className="p-3 flex items-center justify-between">
              <div>
                <p className="font-display text-lg cc-navy">{it.name}</p>
                <p className="text-slate-500 text-xs">{isBox ? it.collection : `${it.price?.toFixed(2)} €`}</p>
              </div>
              <button data-testid={`cat-del-${it.id}`} onClick={() => del(it.id)} className="text-slate-400 hover:text-cc-red p-2"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
